import styles from "./LOSPage.module.css";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import InfoHeader from "../../components/InfoHeader";

import * as Utils from "../../utils";

import { TabBar } from "../../components/TabBar/TabBar";
import { FilterMenu } from "../../components/FilterMenu/FilterMenu";
import { SelectDropdown } from "../../components/SelectDropdown/SelectDropdown";

import Search from "./icons/Search.png";
import { SearchModal } from "../../components/SearchModal/SearchModal";

import { IBOItem } from "../../components/IBOItem/IBOItem";
import { SelectIBOHeader } from "../../components/SelectIBOHeader/SelectIBOHeader";

import { useLOSDetail } from "./useLOSDetail";
import Loader from "components/Loader";

import { useAppDataContext } from "../../App";
import { useTranslation } from "react-i18next";

export const LOSPage = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const passedIbo = location?.state?.iboNum;
  const passedStack = location?.state?.iboStack;
  const showDetailOverride = location?.state?.showDetailView;
  const passedTabState = location?.state?.tabStateByIbo;

  const { iboNumber: appIbo, isLoading: isLoadingIBO } = useAppDataContext();
  const [iboNumber, setiboNumber] = useState(null);

  useEffect(() => {
    if (appIbo) {
      setiboNumber(appIbo);
    }
  }, [appIbo]);

  const [iboStack, setIboStack] = useState(() => {
    if (Array.isArray(passedStack) && passedStack.length > 0)
      return passedStack;
    if (passedIbo) return [passedIbo];
    return [];
  });

  useEffect(() => {
    if (iboNumber && iboStack.length === 0) {
      setIboStack([iboNumber]);
    }
  }, [iboNumber, iboStack]);

  const [showDetailView, setShowDetailView] = useState(
    showDetailOverride !== undefined ? showDetailOverride : iboStack.length > 1
  );

  const [hasClearedState, setHasClearedState] = useState(false);

  useEffect(() => {
    if (location.state && !hasClearedState) {
      history.replace({ ...location, state: {} });
      setHasClearedState(true);
    }
  }, [location, hasClearedState, history]);

  useEffect(() => {
    setShowDetailView(
      showDetailOverride !== undefined
        ? showDetailOverride
        : iboStack.length > 1
    );
  }, [iboStack, showDetailOverride]);

  const currentIbo = iboStack[iboStack.length - 1];

  const { data: losData, isLoading } = useLOSDetail({ ibo: currentIbo });

  const [losList, setLosList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [tabStateByIbo, setTabStateByIbo] = useState(() => {
    if (passedTabState) return passedTabState;

    const ibo = passedIbo || iboNumber;
    return {
      [ibo]: {
        tabSelected: 0,
        sortKey: "Name",
        sortDirection: "Ascending",
      },
    };
  });

  const currentTabState = tabStateByIbo[currentIbo] || {
    tabSelected: 0,
    sortKey: "Name",
    sortDirection: "Ascending",
  };

  const localTabSelected = currentTabState.tabSelected;
  const localSortKey = currentTabState.sortKey;
  const localSortDirection = currentTabState.sortDirection;

  const [isOpen, setIsOpen] = useState(false);
  const [canSelect, setCanSelect] = useState(false);

  const selectOptions = ["Jeff & Britt Scott"];

  useEffect(() => {
    if (!losData) return;

    const tabState = tabStateByIbo[currentIbo] || {
      tabSelected: 0,
      sortKey: "Name",
      sortDirection: "Ascending",
    };

    const {
      tabSelected = 0,
      sortKey = "Name",
      sortDirection = "Ascending",
    } = tabState;

    let updatedList = [...losData.downline];

    switch (tabSelected) {
      case 1:
        updatedList = updatedList.filter((item) => item.isPersonalSponsee);
        break;
      case 2:
        updatedList = updatedList.filter(
          (item) => item.isEagle || item.isDoubleEagle
        );
        break;
      case 3:
        updatedList = updatedList.filter((item) => item.pinLevel === 2);
        break;
    }

    if ((sortKey || "").toLowerCase() === "name") {
      updatedList.sort((a, b) => {
        const nameA = (a.iboName || "").toLowerCase();
        const nameB = (b.iboName || "").toLowerCase();
        const direction = (sortDirection || "").toLowerCase();

        if (direction === "ascending") return nameA.localeCompare(nameB);
        if (direction === "descending") return nameB.localeCompare(nameA);
        return nameA.localeCompare(nameB);
      });
    }

    setLosList(updatedList);
  }, [losData, tabStateByIbo, currentIbo]);

  const onMoreClicked = () => {
    setCanSelect(!canSelect);
  };

  const onSelectClicked = (iboNum) => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(iboNum)
        ? prevSelected.filter((num) => num !== iboNum)
        : [...prevSelected, iboNum]
    );
  };

  const onIboClick = (iboNum) => {
    if (iboNum === currentIbo) return;

    setIboStack((prevStack) => [...prevStack, iboNum]);

    setTabStateByIbo((prev) => ({
      ...prev,
      [iboNum]: prev[iboNum] || {
        tabSelected: 0,
        sortKey: "Name",
        sortDirection: "Ascending",
      },
    }));

    setCanSelect(false);
  };

  const onBack = () => {
    setIboStack((prevStack) => {
      if (prevStack.length > 1) {
        return prevStack.slice(0, -1);
      }
      return prevStack;
    });
    setCanSelect(false);
  };

  const onTabChange = (selectedTab) => {
    setTabStateByIbo((prev) => ({
      ...prev,
      [currentIbo]: {
        ...prev[currentIbo],
        tabSelected: selectedTab,
      },
    }));
  };

  const onFilterChange = (optionsSelected) => {
    setTabStateByIbo((prev) => ({
      ...prev,
      [currentIbo]: {
        ...prev[currentIbo],
        sortKey: optionsSelected[0],
        sortDirection: optionsSelected[1],
      },
    }));
  };

  if (isLoading || !losData) {
    return (
      <div className={styles.loaderContainer}>
        <Loader color="#22252e" />
      </div>
    );
  }

  const avatars = [];
  if (losData.iboInfo.iboAAvatar) avatars.push(losData.iboInfo.iboAAvatar);
  if (losData.iboInfo.iboBAvatar) avatars.push(losData.iboInfo.iboBAvatar);

  return (
    <div className={styles.container}>
      <SearchModal isOpen={isOpen} setIsOpen={setIsOpen} />
      <InfoHeader
        pinLevel={losData.iboInfo.loa.pinLevel}
        firstName={
          losData.iboInfo.iboName
            ? losData.iboInfo.iboName.trim().split(" ").slice(0, -1).join(" ")
            : ""
        }
        lastName={
          losData.iboInfo.iboName
            ? losData.iboInfo.iboName.trim().split(" ").slice(-1)[0]
            : ""
        }
        iboNum={losData.iboInfo.ibo}
        avatar={avatars}
        onMore={() => onMoreClicked()}
        {...(showDetailView
          ? {
              onDetails: () =>
                history.push(Utils.PATHS.LOS_DETAILS_INFO, {
                  iboNum: losData.iboInfo.ibo,
                  fromLOS: true,
                  iboStack,
                  tabStateByIbo,
                }),
              onBack,
            }
          : {})}
      />

      <div className={styles.barWrapper}></div>

      {!canSelect && (
        <div className={styles.tabBarWrapper}>
          <TabBar
            onClick={onTabChange}
            selectedTab={localTabSelected}
            changeView={() => history.push("/los-tree-view")}
            chartView={true}
            downlineCountsData={losData.iboInfo.downlineCounts}
            isLOS={true}
          />
        </div>
      )}

      {!canSelect && (
        <div className={styles.searchAndFilter}>
          <SelectDropdown
            placeholder={selectOptions[0]}
            options={selectOptions}
            onChange={() => {}}
            makeThin={true}
          />
          <div className={styles.filter}></div>
          <FilterMenu
            groups={[
              {
                name: t("Filters"),
                items: [
                  "Name",
                  "Trending",
                  "Date",
                  "Active",
                  "Inactive",
                  "No Sponsor"
                ],
              },
              { name: t("Sorts"), items: ["Ascending", "Descending"] },
            ]}
            onChange={onFilterChange}
            selected={[localSortKey, localSortDirection]}
          />
          <div className={styles.search}>
            <img src={Search} onClick={() => history.push("los-search")} />
          </div>
        </div>
      )}

      {canSelect && (
        <SelectIBOHeader
          onSelectAll={() => {
            const allIbos = losList.map((item) => item.ibo);
            const allSelected = allIbos.every((ibo) =>
              selectedItems.includes(ibo)
            );
            setSelectedItems(allSelected ? [] : allIbos);
          }}
          onDone={() => {
            setSelectedItems([]);
            onMoreClicked();
          }}
        />
      )}

      <div className={styles.iboList}>
        {losList.map((item) => (
          <div className={styles.ibo} key={item.ibo}>
            <IBOItem
              pinLevel={item.pinLevel}
              firstNames={
                item.iboName
                  ? item.iboName.includes(" & ")
                    ? item.iboName
                        .trim()
                        .substring(0, item.iboName.trim().lastIndexOf(" "))
                        .split(" & ")
                        .map((n) => n.trim())
                    : item.iboName.trim().split(" ").slice(0, -1)
                  : []
              }
              lastNames={
                item.iboName
                  ? [item.iboName.trim().split(" ").slice(-1)[0]]
                  : []
              }
              avatars={
                !item.iboBAvatar
                  ? [item.iboAAvatar]
                  : [item.iboAAvatar, item.iboBAvatar]
              }
              iboNum={item.ibo}
              isEagle={item.isEagle}
              isDoubleEagle={item.isDoubleEagle}
              selected={selectedItems.includes(item.ibo)}
              trend={item.trend}
              isComplimentary={item.isComplimentary}
              onSelect={canSelect ? () => onSelectClicked(item.ibo) : undefined}
              onClick={!canSelect ? () => onIboClick(item.ibo) : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

LOSPage.propTypes = {};
